import React, { useEffect, useState } from "react";
import { io, Socket } from "socket.io-client";
import Header from "../../components/Header/Header";
import Footer from "../../components/footer/Footer";
import styles from "./VisualizarConversa.module.css";
import { useTranslation } from "react-i18next";
import Seta from "../../assets/SetaDireita.svg";
import InboxItem from "../../components/InboxChat/InboxChat";
import Chat from "../../components/Chat/Chat";
import api, { socketBaseUrl } from "../../utils/axios/api";

interface Mensagem {
  senderName: string;
  messagePreview: string;
  unreadCount: number;
  profileImage: string;
  data: Date;
  chatType: "ORDER";
  chatId: string;
  otherUserId: string;
}

interface ChatInfo {
  chatType: "ORDER";
  chatId: string;
  senderName: string;
  otherUserId: string;
}

function VisualizarConversaCliente() {
  const opcoesOrdenacao = ["Mais recentes", "Mais antigos", "A-Z", "Z-A"];
  const { t } = useTranslation();
  const [ordenacaoSelecionada, setOrdenacaoSelecionada] = useState(
    opcoesOrdenacao[0]
  );
  const [conversas, setConversas] = useState<Mensagem[]>([]);
  const [conversasOrdenadas, setConversasOrdenadas] = useState<Mensagem[]>([]);
  const [chatAberto, setChatAberto] = useState<ChatInfo | null>(null);

  const [socket, setSocket] = useState<Socket | null>(null);
  const userId = localStorage.getItem("userId");
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState<boolean>(true);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(
    window.innerWidth < 768
  );

  function handleWindowSizeChange() {
    setIsSmallScreen(window.innerWidth < 768);
  }

  useEffect(() => {
    window.addEventListener("resize", handleWindowSizeChange);
    // Execute once to set initial state
    handleWindowSizeChange();

    return () => window.removeEventListener("resize", handleWindowSizeChange);
  }, []);

  useEffect(() => {
    // Initialize socket
    const newSocket = io(socketBaseUrl);
    setSocket(newSocket);    

    // Perform login
    if (userId) {
      newSocket.emit("login", userId);
    }

    // Cleanup on unmount
    return () => {
      newSocket.disconnect();
    };
  }, [userId]);

  useEffect(() => {
    async function loadChats() {
      try {
        if (userId) {
          // Fetch user chats
          const response = await api.get(`/chat/user/${userId}`);
          // Map to Mensagem interface
          const conversasComTipos: Mensagem[] = response.data.map(
            (conversa: any) => ({
              senderName: conversa.senderName,
              messagePreview: conversa.messagePreview,
              unreadCount: conversa.unreadCount,
              profileImage: conversa.profileImage,
              data: new Date(conversa.data),
              chatType: conversa.chatType, // Will be 'ORDER'
              chatId: conversa.chatId,
              otherUserId: conversa.otherUserId,
            })
          );
          setConversas(conversasComTipos);
          ordenarConversas(ordenacaoSelecionada, conversasComTipos);
        }
      } catch (error) {
        console.error("Erro ao carregar conversas:", error);
      }
    }

    loadChats();
  }, [userId]);

  useEffect(() => {
    ordenarConversas(ordenacaoSelecionada, conversas);
  }, [ordenacaoSelecionada, conversas]);

  const ordenarConversas = (opcao: string, conversasToSort: Mensagem[]) => {
    const copia = [...conversasToSort];
    switch (opcao) {
      case "Mais recentes":
        copia.sort((a, b) => b.data.getTime() - a.data.getTime());
        break;
      case "Mais antigos":
        copia.sort((a, b) => a.data.getTime() - b.data.getTime());
        break;
      case "A-Z":
        copia.sort((a, b) => a.senderName.localeCompare(b.senderName));
        break;
      case "Z-A":
        copia.sort((a, b) => b.senderName.localeCompare(a.senderName));
        break;
      default:
        break;
    }
    setConversasOrdenadas(copia);
  };

  const handleOrdenacaoChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setOrdenacaoSelecionada(event.target.value);
  };

  const handleChatClick = (conversa: Mensagem) => {
    setChatAberto({
      chatType: conversa.chatType,
      chatId: conversa.chatId,
      senderName: conversa.senderName,
      otherUserId: conversa.otherUserId,
    });

    // Mark messages as read via Socket
    if (userId && conversa.chatId && socket) {
      socket.emit(
        "markAsRead",
        {
          userId: userId,
          chatId: conversa.chatId, // Correct chatId
        },
        (response: { success: boolean; count: number }) => {
          if (response.success) {
            console.log(`Mensagens marcadas como lidas: ${response.count}`);
            // Update local state
            setConversas((prevConversas) =>
              prevConversas.map((c) =>
                c.chatId === conversa.chatId ? { ...c, unreadCount: 0 } : c
              )
            );
          } else {
            console.error("Erro ao marcar mensagens como lidas:", response);
          }
        }
      );
    }
  };

  // Listener for read updates
  useEffect(() => {
    if (socket) {
      // Listen for read updates
      socket.on(
        "readUpdated",
        ({ chatId, count }: { chatId: string; count: number }) => {
          setConversas((prevConversas) =>
            prevConversas.map((c) =>
              c.chatId === chatId ? { ...c, unreadCount: count } : c
            )
          );
        }
      );

      return () => {
        socket.off("readUpdated");
      };
    }
  }, [socket]);

  useEffect(() => {
    if (chatAberto && socket) {
      socket.on("chat message", (message: any) => {
        // Verify if message belongs to the user
        if (
          message.receiverUserId === userId ||
          message.senderUserId === userId
        ) {
          const currentChatId = message.orderId; // Only 'ORDER'

          setConversas((prevConversas) => {
            const conversaIndex = prevConversas.findIndex(
              (c) => c.chatId === currentChatId
            );

            if (conversaIndex !== -1) {
              const updatedConversas = [...prevConversas];
              updatedConversas[conversaIndex].messagePreview = message.message;
              updatedConversas[conversaIndex].data = new Date(
                message.createdAt
              );

              // Reset unreadCount if chat is open
              if (chatAberto && chatAberto.chatId === currentChatId) {
                updatedConversas[conversaIndex].unreadCount = 0;
              } else if (message.senderUserId !== userId) {
                // Increment unreadCount if message is from sender
                updatedConversas[conversaIndex].unreadCount += 1;
              }

              return updatedConversas;
            } else {
              // Add new conversation
              return [
                ...prevConversas,
                {
                  senderName:
                    message.senderUserId === userId
                      ? "Você"
                      : message.senderName,
                  messagePreview: message.message,
                  unreadCount:
                    chatAberto && chatAberto.chatId === currentChatId
                      ? 0
                      : message.senderUserId !== userId
                      ? 1
                      : 0,
                  profileImage:
                    message.senderUserId !== userId
                      ? message.senderImage || ""
                      : "",
                  data: new Date(message.createdAt),
                  chatType: message.chatType, // Will be 'ORDER'
                  chatId: currentChatId,
                  otherUserId:
                    message.senderUserId === userId
                      ? message.receiverUserId
                      : message.senderUserId,
                },
              ];
            }
          });
        }
      });

      return () => {
        socket.off("chat message");
      };
    }
  }, [chatAberto, socket, userId]);

  const handleCloseChat = () => {
    setChatAberto(null);
  };

  return (
    <div>
      <Header
        boxShadow
        mobileMenuActiveState={isMobileMenuVisible}
        toggleMobileMenu={setIsMobileMenuVisible}
        isSmallScreen={isSmallScreen}
        hamburgerIcon={false}
      />

      <div className={styles.visualizarchat}>
        <div className={styles.content}>
          <div className={styles.caminho}>
            <div className={styles.item}>
              <a href="/"> {t("visualizarConversa.myaccount")} </a>
            </div>
            <div className={styles.imgSetacarrinho}>
              <img
                className={styles.imagemSetacarrinho}
                src={Seta}
                alt="seta"
              />
            </div>
            <div className={styles.local}>
              <span> {t("visualizarConversa.chat")} </span>
            </div>
          </div>
          <div className={styles.firstLine}>
            <h1 className={styles.titulo}>{t("visualizarConversa.chat")}</h1>
            <div className="custom-select"></div>
            <select
              value={ordenacaoSelecionada}
              onChange={handleOrdenacaoChange}
            >
              {opcoesOrdenacao.map((opcao, index) => (
                <option key={index} value={opcao}>
                  {opcao}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.chat}>
            {conversasOrdenadas.map((conversa, index) => (
              <InboxItem
                key={index}
                senderName={conversa.senderName}
                messagePreview={conversa.messagePreview}
                unreadCount={conversa.unreadCount}
                profileImage={conversa.profileImage}
                date={`${conversa.data.getDate()}/${
                  conversa.data.getMonth() + 1
                }/${conversa.data.getFullYear()}`}
                onClick={() => handleChatClick(conversa)} // Passing onClick
              />
            ))}
          </div>
        </div>
      </div>

      {/* Render Chat */}
      {chatAberto && socket && (
        <div className={styles.chatFixed}>
          <Chat
            socket={socket}
            onClose={handleCloseChat}
            infos={{
              otherUserId: chatAberto.otherUserId, // Use correct other user ID
              userName: chatAberto.senderName,
            }}
            type={chatAberto.chatType}
            id={chatAberto.chatId}
          />
        </div>
      )}

      {!isSmallScreen && <Footer />}
    </div>
  );
}

export default VisualizarConversaCliente;
